import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTriggerWordRule,
  TriggerLanguage,
  TriggerMatchType,
  TriggerSeverity,
  TriggerWordRule,
} from '@mmf/contracts';
import { createTriggerWordRule, fetchTriggerWordRules, updateTriggerWordRule } from '../api/client';

const MATCH_TYPES: TriggerMatchType[] = ['Word', 'Phrase', 'Regex'];
const SEVERITIES: TriggerSeverity[] = ['Block', 'Flag', 'Monitor'];
const LANGUAGES: TriggerLanguage[] = ['en', 'af', 'zu', 'xh'];

/**
 * Administrator-only. This is what actually lets someone manage the
 * trigger-word list without a deployment — see the comment on
 * DEFAULT_TRIGGER_RULES in @mmf/contracts for why the real profanity /
 * discriminatory-language word lists are deliberately not seeded (unlike
 * Harassment & Abuse and Threats & Aggression, which are) and have to be
 * added here instead.
 */
export function TriggerWordRuleAdmin(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);

  const rules = useQuery({ queryKey: ['admin', 'trigger-rules'], queryFn: fetchTriggerWordRules });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'trigger-rules'] });
  };

  if (rules.isLoading) {
    return <p role="status">Loading trigger-word rules…</p>;
  }

  if (rules.error) {
    return (
      <p role="alert" data-testid="trigger-rules-error">
        Could not load the trigger-word rules.
      </p>
    );
  }

  return (
    <section aria-labelledby="trigger-rules-heading" data-testid="trigger-word-rule-admin">
      <h3 id="trigger-rules-heading">Trigger-word rules</h3>
      <p className="hint">
        Block holds a post from publication until a moderator approves it. Flag and Monitor
        publish immediately — Flag also queues it for review, Monitor just logs the match.
      </p>

      {!isAdding && (
        <button
          type="button"
          className="button add-trigger-rule-button"
          onClick={() => setIsAdding(true)}
          data-testid="add-trigger-rule"
        >
          Add a rule
        </button>
      )}

      {isAdding && (
        <NewRuleForm
          onCancel={() => setIsAdding(false)}
          onCreated={async () => {
            setIsAdding(false);
            await invalidate();
          }}
        />
      )}

      <ul className="trigger-rule-list">
        {rules.data?.map((rule) => <RuleRow key={rule.id} rule={rule} onSaved={invalidate} />)}
      </ul>
    </section>
  );
}

function NewRuleForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void | Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const [pattern, setPattern] = useState('');
  const [matchType, setMatchType] = useState<TriggerMatchType>('Word');
  const [severity, setSeverity] = useState<TriggerSeverity>('Flag');
  const [language, setLanguage] = useState<TriggerLanguage>('en');

  const create = useMutation({
    mutationFn: () => {
      const input: CreateTriggerWordRule = { pattern, matchType, severity, language, isAllowList: false };
      return createTriggerWordRule(input);
    },
    onSuccess: () => onCreated(),
  });

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    create.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="new-trigger-rule-form">
      {create.error && (
        <p role="alert" className="notice warning" data-testid="new-rule-error">
          Could not save that rule. Try again.
        </p>
      )}

      <label htmlFor="new-rule-pattern">Pattern</label>
      <input
        id="new-rule-pattern"
        value={pattern}
        required
        onChange={(event) => setPattern(event.target.value)}
      />

      <div className="row">
        <span>
          <label htmlFor="new-rule-match-type">Match type</label>
          <select
            id="new-rule-match-type"
            value={matchType}
            onChange={(event) => setMatchType(event.target.value as TriggerMatchType)}
          >
            {MATCH_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </span>

        <span>
          <label htmlFor="new-rule-severity">Severity</label>
          <select
            id="new-rule-severity"
            value={severity}
            onChange={(event) => setSeverity(event.target.value as TriggerSeverity)}
          >
            {SEVERITIES.map((sev) => (
              <option key={sev} value={sev}>
                {sev}
              </option>
            ))}
          </select>
        </span>

        <span>
          <label htmlFor="new-rule-language">Language</label>
          <select
            id="new-rule-language"
            value={language}
            onChange={(event) => setLanguage(event.target.value as TriggerLanguage)}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </span>
      </div>

      <div className="row">
        <button
          type="submit"
          className="button"
          disabled={create.isPending || pattern.trim() === ''}
          data-testid="submit-new-rule"
        >
          {create.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RuleRow({
  rule,
  onSaved,
}: {
  rule: TriggerWordRule;
  onSaved: () => void | Promise<void>;
}): React.JSX.Element {
  const [severity, setSeverity] = useState<TriggerSeverity>(rule.severity);
  const [isActive, setIsActive] = useState(rule.isActive);

  const isDirty = severity !== rule.severity || isActive !== rule.isActive;

  const save = useMutation({
    mutationFn: () => updateTriggerWordRule(rule.id, { severity, isActive }),
    onSuccess: () => onSaved(),
  });

  return (
    <li className="trigger-rule-row" data-testid={`rule-${rule.id}`}>
      <div>
        <code>{rule.pattern}</code>
        <span className="hint">
          {' '}
          {rule.matchType} · {rule.language}
          {rule.isAllowList ? ' · allow-list' : ''}
        </span>
      </div>

      <div className="row">
        <span>
          <label htmlFor={`rule-severity-${rule.id}`}>Severity</label>
          <select
            id={`rule-severity-${rule.id}`}
            value={severity}
            disabled={rule.isAllowList}
            onChange={(event) => setSeverity(event.target.value as TriggerSeverity)}
          >
            {SEVERITIES.map((sev) => (
              <option key={sev} value={sev}>
                {sev}
              </option>
            ))}
          </select>
        </span>

        <span className="checkbox">
          <input
            id={`rule-active-${rule.id}`}
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          <label htmlFor={`rule-active-${rule.id}`}>Active</label>
        </span>

        {/* Only shown once there's actually something to save — a permanently
            disabled button on every single row (most rules, most of the
            time) is clutter, not a control. */}
        {isDirty && (
          <button
            type="button"
            className="button small"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            data-testid={`save-rule-${rule.id}`}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>

      {save.error && (
        <p role="alert" className="notice warning">
          Could not save that change.
        </p>
      )}
    </li>
  );
}
